import { notification } from "antd";
import history from "../../../history";
import { trackApiError, trackApiSlow } from "services/UsageTrackingService";

// Config
const ENTRY_ROUTE = "/auth/login";
//const PUBLIC_REQUEST_KEY = "public-request";

const errorCodeMessages = {
  204: "Não encontrado",
  401: "Falha na autenticação",
  400: "Falha na requisição",
  404: "Inexistente",
  410: "Esta função está na dashboard v2. Nada foi feito aqui.",
  500: "Erro no servidor",
  508: "Estouro de tempo limite",
};

// Rotas de log em background: falhas nelas não devem gerar toast para o usuário
// (não são acionáveis) e, com o tracking de toasts ativo, exibir toast aqui
// criaria um loop (toast de erro -> log -> falha -> toast de erro -> ...).
const silentErrorRoutes = ["/sendLog", "/sendGenericLog"];

// Latência acima disso vira evento API_SLOW (degradação percebida antes do downtime).
const SLOW_THRESHOLD_MS = 5000;

// Retentativa automática: apenas GETs (idempotentes) com falha transitória.
const MAX_RETRIES = 2;
const RETRY_DELAYS_MS = [500, 1500];

function isSilentRoute(url) {
  return silentErrorRoutes.some((route) => (url || "").endsWith(route));
}

function elapsedMs(config) {
  const started = config?.metadata?.startTime;
  return started ? Date.now() - started : undefined;
}

/**
 * Interceptor de sucesso: mede a latência (registrada em API_SLOW acima do
 * threshold) e desembrulha o response.data, como as instâncias já faziam.
 */
export function responseSuccessHandler(response) {
  const config = response?.config || {};
  const durationMs = elapsedMs(config);

  if (durationMs != null && durationMs >= SLOW_THRESHOLD_MS && !isSilentRoute(config.url)) {
    trackApiSlow({
      url: config.url,
      httpMethod: config.method,
      durationMs,
      status: response?.status,
    });
  }

  return response.data;
}

function isRetryable(error) {
  const config = error?.config;
  if (!config) return false;
  if ((config.method || "").toLowerCase() !== "get") return false;
  // Timeout de requisição pesada: repetir só martelaria um backend em dificuldade.
  if (error.code === "ECONNABORTED") return false;
  const status = error?.response?.status;
  // Retenta apenas erro de rede (sem resposta) ou 5xx.
  if (status && status < 500) return false;
  return (config.__retryCount || 0) < MAX_RETRIES;
}

export function responseErrorHandler(error, instance) {
  // Falha transitória em GET idempotente: retenta com backoff antes de
  // incomodar o usuário com toast de erro.
  if (instance && isRetryable(error)) {
    const config = error.config;
    config.__retryCount = (config.__retryCount || 0) + 1;
    config.__isRetry = true;
    const delay =
      RETRY_DELAYS_MS[config.__retryCount - 1] ?? RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1];
    return new Promise((resolve) => setTimeout(resolve, delay)).then(() =>
      instance.request(config)
    );
  }

  let notificationParam = {
    message: "",
  };

  if (error?.response?.data?.Message) {
    notificationParam.message = error.response.data.Message;
  } else if (error?.response?.status === 410 && error?.response?.data?.message) {
    // O 410 das funções que foram para a dashboard v2 ([MovedToDashboardV2] na Lambda) vem em
    // camelCase, e diz qual função e onde ela está agora.
    notificationParam.message = error.response.data.message;
  } else {
    notificationParam.message = errorCodeMessages[error?.response?.status] ?? "Servidor indiponível";
  }

  // Remove token and redirect
  if (error?.response?.status === 401) {
    history.push(ENTRY_ROUTE);
    window.location.reload();
  }

  const requestUrl = error?.config?.url || "";
  const silent = isSilentRoute(requestUrl);

  if (notificationParam.message && !silent) notification.error(notificationParam);

  // Rastreia a falha (inclusive silenciosas: timeout/network). Nunca nas rotas
  // de log, para não gerar loop de tracking.
  if (!silent) {
    trackApiError({
      status: error?.response?.status,
      httpMethod: error?.config?.method,
      url: requestUrl,
      message: notificationParam.message,
      durationMs: elapsedMs(error?.config),
      retries: error?.config?.__retryCount || 0,
    });
  }

  return Promise.reject(error);
}
